import { ArrowDownTrayIcon, ClockIcon } from '@heroicons/react/24/solid';
import React, { useEffect, useState } from 'react'
import { getRfqMessages } from '@/store/slices/rfqSlice';
import { useSelector, useDispatch } from "react-redux";
import DocumentViewModal from '@/components/DocumentViewModal';
import moment from "moment";
import { handleDownloadURL } from '@/components/DownloadFile';
import { useTranslation } from 'react-i18next';


export function OrderChat({ rfq_id, customer_id, purchase_order_file = '', purchase_order_number = '' }) {
  const { t } = useTranslation('order');
  const dispatch = useDispatch();
  const [isViewFileModalOpen, setIsViewFileModalOpen] = useState(false);
  const [documentUrl, setDocumentUrl] = useState('');

  const { rfq_discussion_messages, list_loading, loading } = useSelector((s) => s.rfq);
  const { user } = useSelector((s) => s.user);


  useEffect(() => {
    if (!rfq_id) return;
    dispatch(getRfqMessages(rfq_id));

  }, [rfq_id, rfq_discussion_messages?.[0]?.customer_chat_status]);

  const getInitials = (name = "") => {
    return name
      .trim()
      .split(/\s+/)
      .map(word => word[0].toUpperCase())
      .join("");
  };

  const messagesContainerRef = React.useRef(null);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [rfq_discussion_messages]);


  const isValidUrl = (text) => {
    try {
      new URL(text);
      return true;
    } catch (_) {
      return false;
    }
  };

  return (
    <div className='px-2 md:px-6'>
      <div className="flex flex-wrap gap-3 mb-8">
        {rfq_discussion_messages
          ?.filter(msg => msg.quotations && msg.type === 'quotation' && msg.quotations.length > 0)
          ?.map((msg, index) => {
            const quotation = msg.quotations[0];
            const status = msg?.status;

            const statusClasses = {
              approved: "bg-[#16CB0926] text-[#01B735] border-[#16CB0926]",
              pending: "bg-yellow-50 text-[#f9a825] border-yellow-50",
              rejected: "bg-[#CB090926] text-[#CB0909] border-[#CB090926]",
            };

            const appliedClasses =
              statusClasses[status] ?? "bg-gray-100 text-blue-800 border-gray-300";

            return (
              <div className="flex flex-col items-start gap-1">
                <button
                  key={index}
                  onClick={() => {
                    setIsViewFileModalOpen(true);
                    setDocumentUrl(quotation.file);
                  }}
                  className={`
                  flex items-center gap-2
                  px-4 py-2
                  text-sm font-semibold
                  rounded-lg
                  shadow-sm
                  hover:bg-blue-100
                  hover:border-blue-300
                  hover:shadow-md
                  transition-all duration-200
                  ${appliedClasses}
                `}
                >
                  📄 {quotation.file_name}
                </button>

                {msg.user?.id && (
                  <p className="text-xs text-gray-500 ml-1">
                    {msg.user?.type}
                    <span className="text-gray-400">
                      {" "}({msg.user?.designation})
                    </span>
                  </p>
                )}
              </div>

            );
          })}
        {purchase_order_number && <div className="flex flex-col items-start gap-1">
          <button
            className={`
                flex items-center gap-2
                px-4 py-2
                text-sm font-semibold
                rounded-lg
                shadow-sm
                hover:bg-blue-100
                hover:border-blue-300
                hover:shadow-md
                transition-all duration-200
                bg-[#16CB0926] text-[#01B735] border-[#16CB0926]
              `}
            onClick={() => handleDownloadURL({ fileUrl: purchase_order_file })}
          >
            <ArrowDownTrayIcon className='w-4 h-4 text-blue-400' />  {purchase_order_number}
          </button>

          <p className="text-xs text-gray-500 ml-1">
            {t('purchase_order_uploaded_by_admin')}
          </p>
        </div>}
      </div>

      <div className="bg-[#F4F4F4] border rounded-lg p-1 md:p-4">

        <div className="relative mt-6">

          {(!list_loading && rfq_discussion_messages?.length === 0) && (
            <div className="flex items-center justify-center py-6">
              <p className="text-gray-600 text-sm leading-6">{t('no_messages_start_discussion')}</p>
            </div>
          )}

          {rfq_discussion_messages?.length !== 0 && <>
            {/* Vertical Line */}
            <div className="absolute left-5 top-0 bottom-20 w-0.5 bg-green-500"></div>

            {/* Messages */}
            <div ref={messagesContainerRef} className="space-y-8 max-h-[65vh] overflow-y-auto pr-3 custom-scrollbar">
              {(() => {
                let lastDate = null;
                return (rfq_discussion_messages?.map((msg) => {
                  const currentDateString = moment(msg.message_time).format("DD MMM YYYY");
                  const showDateSeparator = currentDateString !== lastDate;
                  lastDate = currentDateString;

                  return (<React.Fragment key={msg.id}>
                    {showDateSeparator && (
                      <div className="flex justify-start my-4 relative z-20">
                        <div className="text-sm bg-[#CCBDFF] inline-block px-3 py-1 rounded-md font-medium text-[#1601B7] shadow-sm">

                          {moment(msg.message_time).calendar(null, {
                            sameDay: '[Today]',
                            nextDay: '[Tomorrow]',
                            nextWeek: 'dddd',
                            lastDay: '[Yesterday]',
                            lastWeek: '[Last] dddd',
                            sameElse: 'DD MMM YYYY'
                          })}
                        </div>
                      </div>
                    )}
                    <div key={msg.id} className="relative flex gap-4">
                      {/* Icon */}
                      <div className="relative z-10">
                        {msg?.user?.full_name && <div
                          className={`w-10 h-10 flex justify-center items-center text-white font-bold rounded-full ${user.id === msg?.user?.id ? 'bg-green-600' : 'bg-pink-600'}`}
                        >
                          {getInitials(msg.user.full_name)}
                        </div>}
                      </div>

                      {/* Card */}
                      <div className="p-4 flex-1">
                        <span className="absolute top-5 right-4 text-right text-xs text-gray-400 leading-4 flex items-center gap-1">
                          <ClockIcon className="w-4 h-4" />
                          {/* {formatMessageTime(msg.message_time)} */}
                          {moment(msg.message_time).format("DD MMM, YYYY hh:mm A")}
                        </span>

                        {msg.user?.id && <p className="font-semibold text-gray-800 mb-1">{msg.user?.full_name}</p>}
                        {msg.user?.id && <p className="font-small text-gray-400 mb-1">{msg.user?.type}({msg.user?.designation})</p>}

                        {/* <p className="text-gray-600 text-sm leading-6 mb-2">{msg.message}</p> */}
                        <p className="text-gray-600 text-sm leading-6 mb-2">
                          {isValidUrl(msg.message) ? (
                            <a
                              href={msg.message}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 underline"
                            >
                              {msg.message}
                            </a>
                          ) : (
                            msg.message
                          )}
                        </p>

                        {msg.quotations && msg.quotations.length > 0 && (
                          <div className="flex items-start gap-3 flex-wrap mt-2">

                            {/* File list */}
                            <div className="flex flex-col gap-1">
                              {msg.quotations.map((fileItem, index) => (
                                <a
                                  key={index}
                                  onClick={() => {
                                    setIsViewFileModalOpen(true);
                                    setDocumentUrl(fileItem.file);
                                  }}
                                  className="text-blue-600 text-sm underline cursor-pointer hover:text-blue-800"
                                >
                                  {fileItem.file_name}
                                </a>
                              ))}
                            </div>

                            {msg?.status && msg?.type === 'quotation' && (
                              <span
                                className={`${msg.status === 'pending'
                                  ? 'text-orange-500'
                                  : msg.status === 'approved'
                                    ? 'text-green-500'
                                    : 'text-red-500'
                                  } text-sm font-medium`}
                              >
                                {/* {msg.status === 'pending' ? 'Approval Pending' : msg._status} */}
                                {msg.status === 'pending' ? t('approval_pending_status') : msg._status}
                              </span>
                            )}
                          </div>
                        )}

                      </div>
                    </div>
                  </React.Fragment>)
                })
                );
              })()}
            </div>
          </>}

          {list_loading && (
            <div className="flex items-center justify-center py-2">
              <span className="flex space-x-1">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-bounce"></span>
                <span className="w-2 h-2 bg-green-500 rounded-full animate-bounce [animation-delay:0.15s]"></span>
                <span className="w-2 h-2 bg-green-500 rounded-full animate-bounce [animation-delay:0.3s]"></span>
              </span>
            </div>
          )}

        </div>
        {isViewFileModalOpen && <DocumentViewModal documentUrl={documentUrl} setIsDocumentViewModalOpen={setIsViewFileModalOpen}></DocumentViewModal>}
      </div>
    </div>
  )
}

export default OrderChat
